import { faCircle, faLongArrowLeft, faLongArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DateObjectUnits,
  DateTime,
  Duration,
  DurationLikeObject,
  Interval,
  ToObjectOutput,
} from 'luxon'
import React, { useEffect, useState } from 'react'
import './Calendar.scss'

type HTMLDivElementAttributes = Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'>

interface CalendarProps extends HTMLDivElementAttributes {
  value?: DateTime[]
  activeDay?: DateTime
  timezone?: string
  className?: string
  style?: React.CSSProperties
  events?: string[]
  isFollowing?: boolean
  onSelect?: (date: DateTime) => void
  onNavigate?: (calendar: ToObjectOutput) => void
  DayCell?: (props: { className?: string; value: DateTime }) => React.ReactElement
}

export const Calendar = ({
  value,
  activeDay,
  className = '',
  events,
  DayCell,
  isFollowing = true,
  onSelect: handleSelect,
  onNavigate: handleNavigate,
  ...props
}: CalendarProps): React.ReactElement => {
  const [view, setView] = useState<DateTime>(value?.[0] || activeDay || DateTime.local())

  const monthStart: DateTime = view.startOf('month')
  const monthEnd: DateTime = view.endOf('month')
  const monthRange: Interval = Interval.fromDateTimes(monthStart, monthEnd)
  const calendarStart: DateTime = monthStart.startOf('week').minus({ day: 1 }).startOf('day')
  const calendarEnd: DateTime = monthStart
    .plus({ week: 5 })
    .endOf('week')
    .minus({ day: 1 })
    .endOf('day')
  const calendarRange: Interval = Interval.fromDateTimes(calendarStart, calendarEnd)
  const weeks = calendarRange.splitBy({ week: 1 })
  const firstWeek: DateTime[] =
    weeks.length > 0
      ? weeks[0]
          .splitBy({ day: 1 })
          .map((d) => d.start?.startOf('day'))
          .filter((d): d is DateTime => Boolean(d))
      : []

  const onNavigate = (duration: Duration | number | DurationLikeObject): void => {
    const newMonth = monthStart.plus(duration)
    setView(newMonth)
    handleNavigate?.(newMonth.toObject())
  }

  const setNavigate = (duration: DateObjectUnits): void => {
    const newMonth = monthStart.set(duration)
    setView(newMonth)
    handleNavigate?.(newMonth.toObject())
  }

  const onSelect = (dateTime: DateTime): void => {
    dateTime = dateTime.startOf('day')
    handleSelect?.(dateTime)
  }

  const getWeekDay = (day: DateTime) => {
    const dayString = `${day.day}-${day.month}-${day.year}`
    const eventExists = events?.includes(dayString)

    let className: string = 'calendar-cell'
    const dayInterval: Interval = Interval.fromDateTimes(day.startOf('day'), day.endOf('day'))
    if (
      (value && value?.some((v) => dayInterval.contains(v))) ||
      (activeDay && dayInterval.contains(activeDay))
    ) {
      className += ' active'
    }
    if (!monthRange.contains(day)) {
      className += ' text-body-secondary'
    }
    const today = DateTime.fromJSDate(new Date(), { zone: value?.[0]?.zone })
    if (dayInterval.contains(today)) {
      className += ' today'
    }
    return (
      <span
        className={className}
        key={day.day}
        onClick={() => {
          onSelect(day)
        }}
      >
        {DayCell ? (
          <DayCell className="cell-content" value={day}></DayCell>
        ) : (
          <div className="cell-content">{day.day}</div>
        )}
        {eventExists ? (
          <div className="event-marker d-block bg-primary rounded mx-auto my-1" />
        ) : (
          <div className="event-marker d-block mx-auto my-1" />
        )}
      </span>
    )
  }

  const getWeek = (week: Interval) => {
    const days = week
      .splitBy({ day: 1 })
      .map((d) => d.start)
      .filter((d): d is DateTime => Boolean(d))
    return (
      <div className="calendar-row" key={week.start?.weekNumber}>
        {days.map((day: DateTime) => getWeekDay(day))}
      </div>
    )
  }

  const months: DateTime[] = Interval.fromDateTimes(
    DateTime.local().startOf('year'),
    DateTime.local().endOf('year'),
  )
    .splitBy({ month: 1 })
    .map((d) => d.start)
    .filter((d): d is DateTime => Boolean(d))

  const years: DateTime[] = Interval.fromDateTimes(
    DateTime.local().plus({ years: -100 }),
    DateTime.local().plus({ years: 20 }),
  )
    .splitBy({ year: 1 })
    .map((d) => d.start)
    .filter((d): d is DateTime => Boolean(d))

  useEffect(() => {
    if (
      isFollowing &&
      value &&
      (value?.[0]?.month !== view?.month || value?.[0]?.year !== view?.year)
    ) {
      setView(DateTime.fromObject({ year: value?.[0]?.year, month: value?.[0]?.month }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, setView])

  return (
    <div className={['calendar', className].join(' ').trim()} {...props}>
      <div className="calendar-header">
        <div className="calendar-month-title py-2">
          <select
            value={view.month}
            className="form-select form-select-sm"
            onChange={(e) =>
              setNavigate({ month: e?.target?.value ? Number(e?.target?.value) : 0 })
            }
          >
            {months.map((m: DateTime) => (
              <option key={m.month} value={m.month}>
                {m.toFormat('MMMM')}
              </option>
            ))}
          </select>
          <select
            value={view.year}
            className="form-select form-select-sm"
            onChange={(e) => setNavigate({ year: e?.target?.value ? Number(e?.target?.value) : 0 })}
          >
            {years.map((y: DateTime) => (
              <option key={y.year} value={y.year}>
                {y.toFormat('y')}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="btn btn-link text-body"
          onClick={() => onNavigate({ months: -1 })}
        >
          <FontAwesomeIcon icon={faLongArrowLeft} fixedWidth />
        </button>
        {!isFollowing && (
          <button
            type="button"
            className="btn btn-link text-body"
            onClick={() =>
              setView(DateTime.fromObject({ year: value?.[0]?.year, month: value?.[0]?.month }))
            }
          >
            <FontAwesomeIcon icon={faCircle} transform="shrink-8" fixedWidth />
          </button>
        )}
        <button
          type="button"
          className="btn btn-link text-body"
          onClick={() => onNavigate({ months: 1 })}
        >
          <FontAwesomeIcon icon={faLongArrowRight} fixedWidth />
        </button>
      </div>
      <div className="calendar-grid">
        <div className="calendar-row">
          {firstWeek.map((d: DateTime) => (
            <span className="calendar-cell" key={d.day}>
              {d.toFormat('EEEEE')}
            </span>
          ))}
        </div>
        {weeks.map((w) => getWeek(w))}
      </div>
    </div>
  )
}
